// ===== BEGIN FILE: src/components/chat/ChatOrbitalLoader.tsx =====
/**
 * ChatOrbitalLoader — Cosmic "engine working" animation.
 * Three concentric orbital rings with planets, center ShreemAstro pulse.
 * Replaces the countdown timer for the desktop loading state.
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';

interface ChatOrbitalLoaderProps {
  elapsedSeconds: number;
  label?: string;
}

export function ChatOrbitalLoader({ elapsedSeconds, label = 'ShreemAstro AI is consulting the cosmos...' }: ChatOrbitalLoaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, py: 1.5 }}>
      {/* Orbital rings SVG */}
      <Box sx={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
        <svg
          width="64"
          height="64"
          viewBox="0 0 64 64"
          style={{ position: 'absolute', inset: 0 }}
        >
          {/* Outer ring */}
          <circle
            cx="32" cy="32" r="29"
            fill="none"
            stroke="rgba(139,92,246,0.18)"
            strokeWidth="1.5"
          />
          {/* Middle ring */}
          <circle
            cx="32" cy="32" r="21"
            fill="none"
            stroke="rgba(167,139,250,0.22)"
            strokeWidth="1.5"
          />
          {/* Inner ring */}
          <circle
            cx="32" cy="32" r="13"
            fill="none"
            stroke="rgba(196,181,253,0.30)"
            strokeWidth="1.5"
          />
        </svg>

        {/* Outer planet */}
        <Box
          sx={{
            position: 'absolute',
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: '#8b5cf6',
            top: '50%',
            left: '50%',
            marginTop: '-3.5px',
            marginLeft: '-3.5px',
            transformOrigin: '3.5px 3.5px',
            animation: 'orbitOuter 8s linear infinite',
            '@keyframes orbitOuter': {
              from: { transform: 'rotate(0deg) translateX(29px)' },
              to: { transform: 'rotate(360deg) translateX(29px)' }
            }
          }}
        />

        {/* Middle planet */}
        <Box
          sx={{
            position: 'absolute',
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: '#a78bfa',
            top: '50%',
            left: '50%',
            marginTop: '-2.5px',
            marginLeft: '-2.5px',
            transformOrigin: '2.5px 2.5px',
            animation: 'orbitMiddle 5s linear infinite reverse',
            '@keyframes orbitMiddle': {
              from: { transform: 'rotate(120deg) translateX(21px)' },
              to: { transform: 'rotate(480deg) translateX(21px)' }
            }
          }}
        />

        {/* Inner planet */}
        <Box
          sx={{
            position: 'absolute',
            width: 4,
            height: 4,
            borderRadius: '50%',
            bgcolor: '#c4b5fd',
            top: '50%',
            left: '50%',
            marginTop: '-2px',
            marginLeft: '-2px',
            transformOrigin: '2px 2px',
            animation: 'orbitInner 3s linear infinite',
            '@keyframes orbitInner': {
              from: { transform: 'rotate(240deg) translateX(13px)' },
              to: { transform: 'rotate(600deg) translateX(13px)' }
            }
          }}
        />

        {/* Center — ShreemAstro logo pulse */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 20,
            height: 20,
            marginTop: '-10px',
            marginLeft: '-10px',
            borderRadius: '50%',
            overflow: 'hidden',
            animation: 'cosmicPulse 2s ease-in-out infinite',
            '@keyframes cosmicPulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.2)', opacity: 0.85 }
            }
          }}
        >
          <img src="/Logo.png" alt="ShreemAstro" style={{ width: 20, height: 20, borderRadius: '50%' }} />
        </Box>
      </Box>

      {/* Text */}
      <Box>
        <Typography level="body-sm" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
          {label}
        </Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
          {elapsedSeconds > 0 ? `${elapsedSeconds}s elapsed` : 'Consulting your birth chart...'}
        </Typography>
      </Box>
    </Box>
  );
}

export default ChatOrbitalLoader;
// ===== END FILE: src/components/chat/ChatOrbitalLoader.tsx =====
